import { useDispatch, useSelector } from 'react-redux';

import { changePasswordAction } from 'state/current-user/actions/password-change.action';
import { currentUserInfoAction } from 'state/current-user/actions/current-user-info.action';
import { actions as currentUserActions } from 'state/current-user/current-user.slice';
import type { CurrentUserState } from 'state/current-user/current-user.slice';

import { findClient } from 'hooks/api/use-clients';

import { logoutUser } from 'api/generated/users/users';
import { countUserInsights } from 'api/generated/users-insight/users-insight';

import { fetchUnreadUserAssetsCount } from 'hooks/client-asset-api';

import { useOkta } from 'utils/okta/use-okta';

import { clearOktaSessionInfo, getOktaUserId } from 'helpers/okta-session';

import type { RootState, AppDispatch } from 'state/store';

const {
  setNewFilesCountSucceeded,
  setNewInsightsCountSucceeded,
  decrementInsightsCount,
} = currentUserActions;

interface ChangePasswordArgs {
  newPassword: string;
  oldPassword: string;
}

export interface UseCurrentUserResult extends CurrentUserState {
  getInfo: () => void;
  signOut: () => void;
  changePassword: (args: ChangePasswordArgs) => unknown;
  setNewFilesCount: () => void;
  setNewInsightsCount: () => void;
  decrementInsightsCount: () => void;
}

export const useCurrentUser = (): UseCurrentUserResult => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector(
    (state: RootState) => state?.currentUser,
  ) as CurrentUserState;

  const okta = useOkta();

  const navigateToLogin = (): void => {
    window.location.href = '/login';
  };

  return {
    getInfo: () => {
      dispatch(currentUserInfoAction({ find: findClient }));
    },

    signOut: () => {
      const oktaUserId = getOktaUserId();

      (oktaUserId
        ? logoutUser(oktaUserId)
        : Promise.resolve()
      )
        .catch(() => {})
        .finally(() =>
          okta
            .signOut()
            .finally(() => {
              clearOktaSessionInfo();
              navigateToLogin();
            }),
        );
    },

    changePassword: ({ newPassword, oldPassword }) =>
      dispatch(changePasswordAction({ newPassword, oldPassword })),

    setNewFilesCount: () => {
      if (!currentUser.data) return;
      fetchUnreadUserAssetsCount(currentUser.data.oktaUserId).then((count) => {
        dispatch(setNewFilesCountSucceeded({ count }));
      });
    },

    setNewInsightsCount: () => {
      if (!currentUser.data) return;
      countUserInsights(currentUser.data.oktaUserId, Number(currentUser.data.id), {
        'viewed.equals': false,
      }).then((count) => {
        dispatch(setNewInsightsCountSucceeded({ count }));
      });
    },

    decrementInsightsCount: () => {
      dispatch(decrementInsightsCount());
    },

    ...currentUser,
  };
};
